import InvoiceNonPaymentActions, { REPORT_RECEIVED } from 'components/InvoiceNonPaymentActions';
import { deliverInvoicePdf } from '../InvoicePage/invoicePdfDownload';
import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import FixedLoader from 'components/FixedLoader';
import FixedError from 'components/FixedError';
import { useActionApi } from 'hooks';
import Api from 'api';
import { exitReact } from 'utils';
import { PAYMENT_METHOD_OPTIONS, JOB_STATUSES } from 'utils/constants';
import { SimpleModal } from 'components/Modal';
import { StyledButton } from 'components/Buttons/DefaultButton/DefaultButton.styles';
import { CrossButtonBlack } from 'components/Buttons/CrossButton';
import { TitleContainer, PopupTitle, ModalText } from '../ContactSupport/ContactSupport.styles';
import ShiftDetailsTable from './components/ShiftDetailsTable';
import SurveySection from './components/SurveySection';
import NotesSection from './components/NotesSection';
import TalentNavbar from 'components/TalentNavbar';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InvoiceModal from '../InvoicePage/InvoiceModal';
import {
  PageContainer,
  ContentWrapper,
  OfficeName,
  MainCard,
  DoneButton,
  ChatNowSection,
  ChatNowButton,
} from './styles';
import { JobCompletedLocationState, JobCompletedData } from './types';
import dayjs from 'dayjs';
import { PrimaryCtaButton, OutlineCtaButton, TextActionButton } from 'components/Buttons/CtaButtons';
import { isShiftChatOpen } from 'utils/shiftChat';
import {
  canEditTalentFeedback,
  canCompleteTalentSurvey,
  hasRealTalentSubmittedFeedback,
  type TalentSubmittedFeedbackLike,
} from '../../../utils/feedbackEditEligibility';
import { getTalentPaymentViewState, TALENT_PAYMENT_COPY } from 'utils/talentPaymentState';
import useStripeExpressLink from 'hooks/useStripeExpressLink';
import StripeExpressOpenError from 'components/StripeExpressOpenError';
import { getTalentInvoiceTiming, getTalentNonPaymentState } from 'utils/talentInvoiceTiming';
import { useLocalDayClock } from 'hooks/useLocalDayClock';
import { useCompletedJobRefresh } from './useCompletedJobRefresh';

type PaymentMethodValue = (typeof PAYMENT_METHOD_OPTIONS)[number]['value'];

const normalizeFeedbackFlag = (value?: boolean | number | string | null) =>
  value === true || value === 1 || value === '1';

const normalizeRating = (value?: number | string | null) => {
  const rating = Number(value);
  return Number.isFinite(rating) ? rating : 0;
};

/** Includes dismiss-only — used for queue / home prompts, not SurveySection CTA. */
export const computeHasTalentFeedback = (
  job: Pick<JobCompletedData, 'dentistRating' | 'surveyDismissed'>,
) => normalizeFeedbackFlag(job.surveyDismissed) || normalizeRating(job.dentistRating) > 0;

export const computeHasRealTalentSubmittedFeedback = (
  job: TalentSubmittedFeedbackLike,
) => hasRealTalentSubmittedFeedback(job);

/** Real submitted talent feedback (T2-6961 view-only; edits removed). */
export const computeCanEditTalentFeedback = (
  job: Pick<JobCompletedData, 'dentistRating' | 'startDate' | 'shifts'>,
) => canEditTalentFeedback(job);

/** Unsubmitted talent survey still fillable within the 30-day Toronto window (T2-6961). */
export const computeCanCompleteTalentSurvey = (
  job: TalentSubmittedFeedbackLike & Pick<JobCompletedData, 'startDate' | 'shifts'>,
) => canCompleteTalentSurvey(job);

export const buildCompleteSurveyNavigation = (jobData: JobCompletedData, viewOnly = false) => ({
  pathname: 'hygienistFeedback',
  state: {
    job: jobData,
    locationId: (jobData as any).locationId,
    fromJobCompleted: true,
    ...(viewOnly ? { viewOnly: true } : {}),
  },
});



export interface CompletedJobPageData {
  id: number;

  location: {
    practiceName: string;
  };

  /** Payment-terms code (e.g. 7 | 14 | 30) returned by the API */
  paymentTerms: number;
  paymentTermsString: string;

  hygienistId: number;
  stripeActiveStatus: boolean;

  hygienistPrivateNotes?: string;

  dentistRating?: number | string | null;
  surveyDismissed?: boolean | number | string | null;

  shift: {
    shiftDate: string;
    postedStart: string;
    postedEnd: string;
    actualStart?: string;
    actualEnd?: string;
    chatClosesAtUtc?: string | null;
  };
  
  hasInvoice: boolean;

  invoice?: {
    id?: number;
    sentOn?: string;
    hygienistMarkedPaid?: boolean;
    dentistMarkedPaid?: number;
    paidBy?: string | null;
    paidOn?: string;
    nonPaymentReportedOn?: string;
    nonPaymentEscalatedOn?: string | null;
    officeMarkedPaid?: boolean;
    officeMarkedPaidOn?: string | null;
    talentMarkedPaidOn?: string | null;
    paidMethod?: string | null;
    paidNote?: string | null;
    paymentLifecycleId?: number;
    officePaymentStatus?: 'processing' | 'action_required' | 'paid';
    talentPaymentStatus?: 'not_started' | 'processing' | 'action_required' | 'paid';
    talentPaymentMethod?: 'instant' | 'standard' | null;

    /* Financials */
    hourlyRate?: number;
    totalHours?: number;
    totalUnpaidHours?: number;
    totalBillableHours?: number;
    totalInvoiceAmt?: number;
  };

  editedInvoice?: {
    actualStart: string;
    actualEnd: string;
    totalHours: number;
    unpaidHours: number;
    billableHours: number;
    totalAmt: number;
  };
}

const loadCompletedJob = async (jobId: number): Promise<JobCompletedData> => {
  const data = await Api.getCompletedJobPageData(jobId);
  return {
    ...data,
    shifts: data.shift ? [data.shift] : [],
    type: (data.type ?? 0) as number,
    hasFeedback: computeHasTalentFeedback(data),
  };
};

const JobCompletedPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation<JobCompletedLocationState>();
  const { jobId, jobCount } = location.state || {};
  const invoiceClock = useLocalDayClock();
  // T2-7494: "Click here to resolve" on an ACTION REQUIRED payment.
  const {
    opening: resolveOpening,
    failure: resolveFailure,
    open: openStripeToResolve,
  } = useStripeExpressLink();
  const reportingInFlight = useRef(false);
  const activeVisit = useRef({ jobId });
  if (activeVisit.current.jobId !== jobId) activeVisit.current = { jobId };
  const mounted = useRef(true);
  useLayoutEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  
  const [notes, setNotes] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const notesAreDirty = useRef(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [reportConfirmation, setReportConfirmation] = useState(REPORT_RECEIVED);
  const [reportReceipt, setReportReceipt] = useState<{ visit: typeof activeVisit.current; invoiceId: number; reportedOn?: string } | null>(null);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);

  const [unsavedConfirmOpen, setUnsavedConfirmOpen] = useState(false);
  const [nonPaymentConfirmOpen, setNonPaymentConfirmOpen] = useState(false);
  const [markPaidConfirmOpen, setMarkPaidConfirmOpen] = useState(false);
  const [isMarkingPaid, setIsMarkingPaid] = useState(false);
  const [paymentMethodModalOpen, setPaymentMethodModalOpen] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethodValue | null>(null);
  const [otherPaymentMethodText, setOtherPaymentMethodText] = useState('');
  const [isReportingNonPayment, setIsReportingNonPayment] = useState(false);
  const [isDownloadingInvoice, setIsDownloadingInvoice] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [isDownloadErrorOpen, setIsDownloadErrorOpen] = useState(false);

  // Invoice Modal state
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [invoiceModalIsViewMode, setInvoiceModalIsViewMode] = useState(false);
  const [invoiceJobData, setInvoiceJobData] = useState<any>(null);
  const [isLoadingInvoiceData, setIsLoadingInvoiceData] = useState(false);

  // Get current user info for navbar
  const currentUser = (window as any)?.TempStars?.User?.getCurrentUser?.();
  const hygienist = currentUser?.hygienist;
  const firstName = hygienist?.firstName || '';
  const lastName = hygienist?.lastName || '';
  const photoUrl = hygienist?.photoUrl || undefined;
  const isHygOrAsst = hygienist?.type === (window as any).TempStars?.HYGIENIST_TYPES?.HYGIENIST || 
                      hygienist?.type === (window as any).TempStars?.HYGIENIST_TYPES?.ASSISTANT;

  useEffect(() => {
    if (!jobId) {
      history.push({ pathname: 'hygienistHome' });
    }
  }, [jobId, history]);

  useEffect(() => {
    notesAreDirty.current = false;
    setHasUnsavedChanges(false);
    setNotes('');
    reportingInFlight.current = false;
    setIsReportingNonPayment(false);
    setNonPaymentConfirmOpen(false);
    setSuccessModalOpen(false);
    setErrorModalOpen(false);
  }, [jobId]);

  const [jobData, isLoading, error, refetchJob] = useCompletedJobRefresh(jobId, loadCompletedJob, data => {
    if (!notesAreDirty.current) setNotes(data.hygienistPrivateNotes || '');
  });
  const currentReportReceipt = reportReceipt?.visit === activeVisit.current && reportReceipt.invoiceId === jobData?.invoice?.id ? reportReceipt : null;
  const reportingInvoice = currentReportReceipt?.reportedOn ? { ...jobData?.invoice, nonPaymentReportedOn: currentReportReceipt.reportedOn } : jobData?.invoice;

  useEffect(() => {
    if (jobId) {
      refetchJob();
    }
  }, [jobId, invoiceClock, refetchJob]);

  const [isSaving, saveError, saveNotes] = useActionApi<{ jobId: number; notes: string }, void>({
    apiMethod: Api.updateJobHygienistNotes,
    onSuccess: () => {
      notesAreDirty.current = false;
      setHasUnsavedChanges(false);
      history.push({ pathname: 'hygienistHome' });
    }
  });

  const navigateHome = () => {
    history.push({ pathname: 'hygienistHome' });
  };

  const handleBack = () => {
    if (hasUnsavedChanges) {
      setUnsavedConfirmOpen(true);
      return;
    }
    navigateHome();
  };

  const handleChat = useCallback(async () => {
    if (!jobId) return;
    try {
      const job = await Api.getJobDetails(jobId);
      const currentHygienist = (window as any)?.TempStars?.User?.getCurrentUser?.()?.hygienist;
      history.push({
        pathname: 'hygienistMessenger',
        state: {
          job: {
            ...job,
            hygienist: job.hygienist || currentHygienist,
          },
          returnTo: { pathname: 'completeJob', state: { jobId } },
        },
      });
    } catch (err) {
      console.error('[CompleteJobPage] Failed to open messenger', err);
    }
  }, [history, jobId]);

  const chatJob = useMemo(() => {
    if (!jobData?.id) return null;
    return {
      ...jobData,
      status: jobData.status ?? JOB_STATUSES.COMPLETED,
      startDate: jobData.startDate ?? jobData.shifts?.[0]?.shiftDate,
    };
  }, [jobData]);

  const showChatNow = chatJob ? isShiftChatOpen(chatJob) : false;

  const handleConfirmLeaveWithUnsaved = () => {
    setUnsavedConfirmOpen(false);
    navigateHome();
  };

  const handleCancelLeaveWithUnsaved = () => {
    setUnsavedConfirmOpen(false);
  };

  const handleCompleteSurvey = async () => {
    if (!jobData) return;
    if (computeHasRealTalentSubmittedFeedback(jobData)) return;
    if (!computeCanCompleteTalentSurvey(jobData)) return;

    history.push(buildCompleteSurveyNavigation(jobData));
  };

  const handleViewFeedback = () => {
    if (!jobData) return;
    history.push(buildCompleteSurveyNavigation(jobData, true));
  };

  const handleReportNonPayment = () => {
    if (currentReportReceipt) return;
    if (!getTalentNonPaymentState(jobData?.invoice, jobData?.paymentTerms, jobData?.location?.isCentralBooked).canReport) return;
    setNonPaymentConfirmOpen(true);
  };

  const handleConfirmReportNonPayment = async () => {
    if (reportingInFlight.current || currentReportReceipt) return;
    if (!jobData?.invoice?.id || !getTalentNonPaymentState(jobData.invoice, jobData.paymentTerms, jobData.location?.isCentralBooked).canReport) {
      setNonPaymentConfirmOpen(false);
      return;
    }

    reportingInFlight.current = true;
    const visit = activeVisit.current;
    const isCurrentVisit = () => mounted.current && activeVisit.current === visit;
    setNonPaymentConfirmOpen(false);
    setReportError(null);

    try {
      setIsReportingNonPayment(true);
      const reportResponse = await Api.reportNonPayment(jobData.invoice.id);
      if (!isCurrentVisit()) return;
      setReportReceipt({ visit, invoiceId: jobData.invoice.id, reportedOn: reportResponse?.reportedOn });
      setReportConfirmation(reportResponse?.message || REPORT_RECEIVED);
      setSuccessModalOpen(true);
      try { await refetchJob(); } catch (_) { /* The report is committed; a refresh failure must not invite another submission. */ }
    } catch (err: any) {
      if (!isCurrentVisit()) return;
      const body = err?.body || err?.data || err;
      setReportError(body?.code === 'NON_PAYMENT_GRACE_PERIOD' && typeof body.message === 'string'
        ? body.message : 'Error reporting non-payment. Please try again.');
      setErrorModalOpen(true);
    } finally {
      if (isCurrentVisit()) {
        reportingInFlight.current = false;
        setIsReportingNonPayment(false);
      }
    }
  };

  const handleCancelReportNonPayment = () => {
    setNonPaymentConfirmOpen(false);
  };

  const handleNotesChange = (newNotes: string) => {
    notesAreDirty.current = newNotes !== (jobData?.hygienistPrivateNotes || '');
    setNotes(newNotes);
    setHasUnsavedChanges(notesAreDirty.current);
  };

  const handleDone = () => {
    if (hasUnsavedChanges && jobId) {
      saveNotes({ jobId, notes });
    } else {
      history.push({ pathname: 'hygienistHome' });
    }
  };

  const onInvoiceAction = async () => {
    if (!jobId) return;
    
    setIsLoadingInvoiceData(true);
    try {
      // Fetch full job details for the invoice modal
      const [fetchedJob, invoice] = await Promise.all([
        Api.getJobDetails(jobId),
        Api.getInvoiceForJob(jobId).catch(() => null)
      ]);

      // Merge hygienist data from current user when not present
      const currentHygienist = (window as any)?.TempStars?.User?.getCurrentUser?.()?.hygienist;

      const jobWithData = {
        ...fetchedJob,
        hygienist: fetchedJob.hygienist || currentHygienist,
        invoice: invoice && typeof invoice === 'object' ? invoice : undefined,
        stripeActiveStatus: jobData?.stripeActiveStatus ?? false,
      };

      setInvoiceJobData(jobWithData);
      setInvoiceModalIsViewMode(!!jobData?.hasInvoice);
      setIsInvoiceModalOpen(true);
    } catch (error) {
      console.error('Error fetching invoice data:', error);
    } finally {
      setIsLoadingInvoiceData(false);
    }
  };

  const getInvoiceFilename = useCallback(() => {
    const dateSource = jobData?.shifts?.[0]?.shiftDate || jobData?.invoice?.sentOn;
    const formattedDate = dateSource ? dayjs(dateSource).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');
    return `Invoice_${jobData?.id || jobId}_${formattedDate}.pdf`;
  }, [jobData?.id, jobData?.invoice?.sentOn, jobData?.shifts, jobId]);

  const handleDownloadInvoicePdf = useCallback(async () => {
    const invoiceId = jobData?.invoice?.id;
    if (!invoiceId) return;
    setDownloadError(null);
    setIsDownloadErrorOpen(false);
    setIsDownloadingInvoice(true);
    try {
      const blob = await Api.downloadInvoicePdf(invoiceId);
      await deliverInvoicePdf(blob, getInvoiceFilename());
    } catch (error: any) {
      const fallbackMessage = typeof error?.message === 'string' && error.message.length > 0
        ? error.message
        : 'Unable to download invoice. Please try again.';
      setDownloadError(fallbackMessage);
      setIsDownloadErrorOpen(true);
    } finally {
      setIsDownloadingInvoice(false);
    }
  }, [getInvoiceFilename, jobData?.invoice?.id]);

  const renderDownloadInvoiceButton = () => {
    if (!jobData?.invoice?.id) return null;
    return (
      <div style={{ marginBottom: 8, marginTop: 8 }}>
        <OutlineCtaButton
          onClick={handleDownloadInvoicePdf}
          disabled={isDownloadingInvoice}
          id="complete-job-download-invoice-button"
          data-testid="complete-job-download-invoice-button"
          aria-label="Download Invoice"
        >
          {isDownloadingInvoice ? 'Preparing PDF...' : 'Download Invoice'}
        </OutlineCtaButton>
      </div>
    );
  };

  const handleInvoiceModalClose = () => {
    setIsInvoiceModalOpen(false);
    setInvoiceJobData(null);
  };

  const handleInvoiceSuccess = () => {
    // Refetch job data to update the invoice status
    refetchJob();
    setIsInvoiceModalOpen(false);
    setInvoiceJobData(null);
  };

  const getPaymentTermsText = (days?: number) => {
    switch (days) {
      case 1:
        return 'Within 24hrs of the Shift';
      case 5:
        return 'Within 5 Days of the Shift';
      case 14:
        return 'Within 2 Weeks of the Shift';
      case 30:
        return 'Within 1 Month of the Shift';
      default:
        return 'Within 2 Weeks of the Shift';
    }
  };

  const formatCurrency = (value?: number) => {
    if (value == null) return '-';
    try {
      return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(value);
    } catch {
      return `$${value.toFixed(2)}`;
    }
  };

  // Mark-as-paid flow: confirm then capture payment method
  const handleOpenMarkPaid = () => {
    if (!jobData?.invoice?.id) return;
    setMarkPaidConfirmOpen(true);
  };

  const handleCancelMarkPaid = () => {
    setMarkPaidConfirmOpen(false);
  };

  const resetPaymentMethodState = () => {
    setSelectedPaymentMethod(null);
    setOtherPaymentMethodText('');
  };

  const handleConfirmMarkPaid = () => {
    if (!jobData?.invoice?.id) {
      setMarkPaidConfirmOpen(false);
      return;
    }
    setMarkPaidConfirmOpen(false);
    setPaymentMethodModalOpen(true);
  };

  const handleCancelPaymentMethod = () => {
    setPaymentMethodModalOpen(false);
    resetPaymentMethodState();
  };

  const handleSelectPaymentMethod = (value: PaymentMethodValue) => {
    setSelectedPaymentMethod(value);
    if (value !== 'other') {
      setOtherPaymentMethodText('');
    }
  };

  const handleConfirmPaymentMethod = async () => {
    if (!jobData?.invoice?.id || !selectedPaymentMethod) {
      handleCancelPaymentMethod();
      return;
    }

    const trimmedOther = otherPaymentMethodText.trim();
    if (selectedPaymentMethod === 'other' && trimmedOther.length < 5) {
      return;
    }

    const paidByValue = selectedPaymentMethod === 'other' ? `other: ${trimmedOther}` : selectedPaymentMethod;

    try {
      setIsMarkingPaid(true);
      await Api.updateInvoice({ id: jobData.invoice.id, hygienistMarkedPaid: 1, paidBy: paidByValue });
      await refetchJob();
    } catch (e) {
      setReportError('Error marking this invoice paid. Please try again.');
      setErrorModalOpen(true);
    } finally {
      setIsMarkingPaid(false);
      handleCancelPaymentMethod();
    }
  };

  const RenderPaymentStatusSection = () => {
    const hasInvoice = !!jobData?.hasInvoice;
    const invoice = jobData?.invoice;
    // T2-7494: an office's own mark never makes the invoice PAID for the talent.
    const paymentView = getTalentPaymentViewState(invoice);
    const isPaid = paymentView.isPaid;
    const {
      sentOn,
      dueDate,
      isOverdue,
      daysUntilDue,
    } = getTalentInvoiceTiming(invoice?.sentOn, jobData?.paymentTerms, dayjs(invoiceClock));
    const dayWord = daysUntilDue != null && Math.abs(daysUntilDue) === 1 ? 'day' : 'days';

    if (!hasInvoice) {
      return (
        <div style={{ background: 'var(--common-white-main)', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', margin: '0 16px 16px', padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 16 }}>Invoice Status</h3>
            <span style={{ background: 'var(--background-info)', color: 'var(--text-secondary)', borderRadius: 999, padding: '4px 10px', fontSize: 12 }}>Not Created</span>
          </div>
          <div style={{ background: 'var(--background-alternate)', border: '1px solid var(--primary-main)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
            <div style={{ color: 'var(--primary-main)', fontSize: 14, fontWeight: 600 }}>Action Required</div>
            <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 }}>You need to create and send your invoice to receive payment for this shift.</div>
          </div>
          <PrimaryCtaButton onClick={onInvoiceAction}>
            Create & Send Invoice
          </PrimaryCtaButton>
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--divider)', display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
            <span style={{ color: 'var(--text-secondary)' }}>Office Expected to Pay:</span>
            <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{getPaymentTermsText(jobData?.paymentTerms)}</span>
          </div>
        </div>
      );
    }

    // T2-7494: ACTION REQUIRED only when the Stripe Express account is broken
    // or Stripe reported a payout failure. "Click here to resolve" opens the
    // temp's Stripe Express the same way Profile does (T2-7437 helper).
    if (paymentView.requiresAction) {
      return (
        <div data-testid="talent-payment-action_required" style={{ background: 'var(--common-white-main)', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', margin: '0 16px 16px', padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 16 }}>Payment Status</h3>
            <span style={{ background: 'var(--status-overdue-bg)', color: 'var(--status-overdue-text)', borderRadius: 999, padding: '4px 10px', fontSize: 12 }}>
              {paymentView.label}
            </span>
          </div>
          <div style={{ background: 'var(--background-alternate)', border: '1px solid var(--primary-main)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
            <div style={{ color: 'var(--primary-main)', fontSize: 14, fontWeight: 600 }}>{paymentView.title}</div>
            <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 }}>{paymentView.message}</div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <PrimaryCtaButton
              onClick={() => { openStripeToResolve(); }}
              disabled={resolveOpening}
              data-testid="talent-payment-resolve-button"
            >
              {TALENT_PAYMENT_COPY.resolveButton}
            </PrimaryCtaButton>
            <StripeExpressOpenError failure={resolveFailure} testIdPrefix="talent-payment" />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
            <span style={{ color: 'var(--text-secondary)' }}>Invoice Amount:</span>
            <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{formatCurrency(invoice?.totalInvoiceAmt)}</span>
          </div>
          {renderDownloadInvoiceButton()}
        </div>
      );
    }

    // T2-7494: Pay Now PAID has no title (just the badge and where the money
    // is); a temp-marked invoice says "Payment Received" with the amount, and
    // no payment method, date or Paid On line.
    if (isPaid) {
      return (
        <div data-testid={`talent-payment-${paymentView.display}`} style={{ background: 'var(--common-white-main)', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', margin: '0 16px 16px', padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 16 }}>Payment Status</h3>
            <span style={{ background: 'var(--status-done-bg)', color: 'var(--status-done-text)', borderRadius: 999, padding: '4px 10px', fontSize: 12 }}>{paymentView.label}</span>
          </div>
          <div style={{ background: 'var(--status-done-bg)', border: '1px solid var(--primary-green)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
            {paymentView.title && (
              <div style={{ color: 'var(--primary-green)', fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{paymentView.title}</div>
            )}
            <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{paymentView.message}</div>
          </div>
          <div style={{ display: 'grid', rowGap: 8, fontSize: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--text-secondary)' }}>Invoice Amount:</span>
              <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{formatCurrency(invoice?.totalInvoiceAmt)}</span>
            </div>
          </div>
          {renderDownloadInvoiceButton()}
        </div>
      );
    }

    if (isOverdue) {
      return (
        <div data-testid="talent-payment-overdue" style={{ background: 'var(--common-white-main)', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', margin: '0 16px 16px', padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 16 }}>Payment Status</h3>
            <span style={{ background: 'var(--status-overdue-bg)', color: 'var(--status-overdue-text)', borderRadius: 999, padding: '4px 10px', fontSize: 12 }}>Overdue?</span>
          </div>
          <div style={{ background: 'var(--background-alternate)', border: '1px solid var(--primary-main)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
            <div style={{ color: 'var(--primary-main)', fontSize: 14, fontWeight: 600 }}>Payment May Be Overdue</div>
            <div style={{ color: 'var(--primary-main)', fontSize: 13, marginTop: 4 }}>
              Your invoice was due on {dueDate?.format('MMM D, YYYY')} ({Math.abs(daysUntilDue || 0)} {dayWord} ago). The office may have paid through alternative means.
            </div>
          </div>
          <div style={{ display: 'grid', rowGap: 8, fontSize: 14, marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--text-secondary)' }}>Invoice Amount:</span>
              <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{formatCurrency(invoice?.totalInvoiceAmt)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--text-secondary)' }}>Due Date:</span>
              <span style={{ color: 'var(--primary-main)', fontWeight: 600 }}>{dueDate?.format('MMM D, YYYY')} ({Math.abs(daysUntilDue || 0)} {dayWord} overdue)</span>
            </div>
          </div>
          <div style={{ position: 'relative' }}>
            <div style={{ marginBottom: 8 }}>
              <PrimaryCtaButton onClick={onInvoiceAction}>
                View & Edit Invoice
              </PrimaryCtaButton>
            </div>
            {renderDownloadInvoiceButton()}
            {paymentView.canUseLegacyPaidToggle && (
              <div style={{ marginBottom: 8 }}>
                <PrimaryCtaButton onClick={handleOpenMarkPaid} disabled={isMarkingPaid} data-testid="complete-job-overdue-mark-paid-button">
                  {isMarkingPaid ? 'Marking…' : 'Mark as Paid'}
                </PrimaryCtaButton>
              </div>
            )}
          </div>
        </div>
      );
    }

    // Sent, not yet due
    return (
      <div data-testid="talent-payment-pending" style={{ background: 'var(--common-white-main)', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', margin: '0 16px 16px', padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>Payment Status</h3>
          <span style={{ background: 'var(--status-pending-bg)', color: 'var(--status-pending-text)', borderRadius: 999, padding: '4px 10px', fontSize: 12 }}>Pending</span>
        </div>
        <div style={{ background: 'var(--background-info)', border: '1px solid var(--secondary-main)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
          <div style={{ color: 'var(--secondary-main)', fontSize: 14, fontWeight: 600 }}>Invoice Sent Successfully</div>
          <div style={{ color: 'var(--secondary-main)', fontSize: 13, marginTop: 4 }}>
            {sentOn ? `Your invoice was sent on ${sentOn.format('MMM D, YYYY')}.` : 'Your invoice has been sent.'} Payment is expected within {jobData?.paymentTerms === 1 ? '24 hours' : jobData?.paymentTerms === 5 ? '5 days' : jobData?.paymentTerms === 14 ? '2 weeks' : jobData?.paymentTerms === 30 ? '1 month' : 'the expected timeframe'}.
          </div>
        </div>
        <div style={{ display: 'grid', rowGap: 8, fontSize: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: 'var(--text-secondary)' }}>Invoice Amount:</span>
            <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{formatCurrency(invoice?.totalInvoiceAmt)}</span>
          </div>
          {dueDate && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Expected Payment By:</span>
                <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{dueDate.format('MMM D, YYYY')}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Days Until Due:</span>
                {/* The complete due day remains inside the reporting grace period. */}
                <span style={{ color: 'var(--primary-blue)', fontWeight: 600 }}>
                  {daysUntilDue != null && daysUntilDue <= 0 ? 'Today' : `${daysUntilDue} ${dayWord}`}
                </span>
              </div>
            </>
          )}
        </div>
        <div style={{ marginTop: 12 }}>
          <PrimaryCtaButton onClick={onInvoiceAction}>
            View & Edit Invoice
          </PrimaryCtaButton>
        </div>
        {renderDownloadInvoiceButton()}
        {paymentView.canUseLegacyPaidToggle && (
          <div style={{ marginTop: 12 }}>
            <TextActionButton
              onClick={handleOpenMarkPaid}
              disabled={isMarkingPaid}
              data-testid="complete-job-pending-mark-paid-button"
            >
              {isMarkingPaid ? 'Marking…' : 'Mark as Paid (if paid outside TempStars)'}
            </TextActionButton>
          </div>
        )}
      </div>
    );
  };

  if (isLoading && !jobData?.id) {
    return <FixedLoader loading={true} />;
  }

  if (!jobData?.id) {
    return (
      <>
        <TalentNavbar
          firstName={firstName}
          lastName={lastName}
          photoUrl={photoUrl}
          isHygOrAsst={isHygOrAsst}
          instabookActivated={hygienist?.InstabookActivated === 1}
          isTestAccount={(hygienist as any)?.isTest === 1}
          showAddShiftButton={false}
          showJobBoardButton={false}
        />
        <PageContainer>
          <div className="headerRow">
            <button 
              className="backButton no-fastclick" 
              onClick={handleBack}
              aria-label="Back"
            >
              <ArrowBackIcon />
              <span>Back</span>
            </button>
            <h1 className="pageTitle">Completed Shift</h1>
          </div>
          <FixedError error={error || new Error("Error loading job details")} />
          <PrimaryCtaButton onClick={refetchJob}>Try Again</PrimaryCtaButton>
        </PageContainer>
      </>
    );
  }

  const trimmedPaymentMethodOther = otherPaymentMethodText.trim();
  const isOtherPaymentSelected = selectedPaymentMethod === 'other';
  const isPaymentMethodValid = !!selectedPaymentMethod && (!isOtherPaymentSelected || trimmedPaymentMethodOther.length >= 5);
  const showOtherHelper = isOtherPaymentSelected && trimmedPaymentMethodOther.length < 5;
  const officeName = jobData.location?.practiceName || jobData.location?.name || `Office ${jobData.location?.id || ''}`;
  const persistedReporting = getTalentNonPaymentState(reportingInvoice, jobData.paymentTerms, jobData.location?.isCentralBooked, dayjs(invoiceClock));
  const reporting = { ...persistedReporting, canReport: persistedReporting.canReport && !currentReportReceipt,
    reported: persistedReporting.reported || !!currentReportReceipt };

  return (
    <>
      <TalentNavbar
        firstName={firstName}
        lastName={lastName}
        photoUrl={photoUrl}
        isHygOrAsst={isHygOrAsst}
        instabookActivated={hygienist?.InstabookActivated === 1}
        isTestAccount={(hygienist as any)?.isTest === 1}
        showAddShiftButton={false}
        showJobBoardButton={false}
      />
      <PageContainer>
        <div className="headerRow">
          <button 
            className="backButton no-fastclick" 
            onClick={handleBack}
            aria-label="Back"
          >
            <ArrowBackIcon />
            <span>Back</span>
          </button>
          <h1 className="pageTitle">Completed Shift</h1>
        </div>

      <ContentWrapper>
        {error && (
          <MainCard role="status" data-testid="complete-job-refresh-error">
            <p>We couldn't refresh these details. Your notes are still here.</p>
            <TextActionButton onClick={refetchJob}>Try Again</TextActionButton>
          </MainCard>
        )}
        <OfficeName style={{ textAlign: 'center', margin: '24px 16px 24px' }}>
          {officeName}
        </OfficeName>

        <MainCard>
          <ShiftDetailsTable 
            shifts={jobData.shifts} 
            hasInvoice={jobData.hasInvoice}
            hourlyRate={jobData.hourlyRate}
          />
        </MainCard>

        <RenderPaymentStatusSection />

        {(reporting.canReport || reporting.reported || reporting.showGraceMessage) && (
          <MainCard data-testid="complete-job-non-payment-section">
            {reporting.showGraceMessage && (
              <p data-testid="complete-job-non-payment-grace-message">{reporting.graceMessage}</p>
            )}
            {reporting.reported ? (
              <div data-testid="complete-job-non-payment-reported">{reportingInvoice?.nonPaymentReportedOn
                ? <InvoiceNonPaymentActions invoice={reportingInvoice} paymentTerms={jobData.paymentTerms}
                  isCentralBooked={jobData.location?.isCentralBooked} onRefresh={refetchJob} followUpOnly />
                : <p>Your report has been received. Refresh the invoice to see the latest details.</p>}</div>
            ) : reporting.canReport && (
              <>
                <OutlineCtaButton
                  data-testid="complete-job-report-non-payment-button"
                  onClick={handleReportNonPayment}
                  disabled={isReportingNonPayment}
                >
                  {isReportingNonPayment ? 'Reporting…' : 'Report Non-Payment'}
                </OutlineCtaButton>
                <p>Reporting non-payment will notify the office and resend your invoice. You can escalate to support tomorrow if payment has not arrived.</p>
              </>
            )}
          </MainCard>
        )}

        {showChatNow && (
          <ChatNowSection>
            <ChatNowButton type="button" className="no-fastclick" onClick={handleChat}>
              Chat Now
            </ChatNowButton>
          </ChatNowSection>
        )}

        <SurveySection
          hasFeedback={computeHasRealTalentSubmittedFeedback(jobData)}
          canCompleteSurvey={computeCanCompleteTalentSurvey(jobData)}
          onCompleteSurvey={handleCompleteSurvey}
          onViewFeedback={handleViewFeedback}
        />

        <NotesSection
          notes={notes}
          professionalName={officeName}
          onNotesChange={handleNotesChange}
          onSave={handleDone}
          isSaving={isSaving}
        />

        <DoneButton 
          onClick={handleDone}
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Done'}
        </DoneButton>

        <SimpleModal isOpen={unsavedConfirmOpen}>
          <>
            <TitleContainer>
              <PopupTitle>Unsaved notes</PopupTitle>
              <CrossButtonBlack onClick={handleCancelLeaveWithUnsaved} />
            </TitleContainer>
            <ModalText>
              You have unsaved notes. Are you sure you want to leave?
            </ModalText>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
              <StyledButton
                variant="outlined"
                size="large"
                onClick={handleCancelLeaveWithUnsaved}
                className="no-fastclick"
              >
                Stay
              </StyledButton>
              <StyledButton
                variant="contained"
                size="large"
                onClick={handleConfirmLeaveWithUnsaved}
                className="no-fastclick"
              >
                Leave
              </StyledButton>
            </div>
          </>
        </SimpleModal>

        <SimpleModal isOpen={nonPaymentConfirmOpen}>
          <>
            <TitleContainer>
              <PopupTitle>Report Non-Payment?</PopupTitle>
              <CrossButtonBlack onClick={handleCancelReportNonPayment} />
            </TitleContainer>
            <ModalText>
              If this invoice is unpaid beyond the time specified in the shift posting, tap "OK" to confirm. The office will receive a reminder and a copy of your invoice. If payment has not arrived tomorrow, you can escalate to support.
            </ModalText>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
              <StyledButton
                variant="outlined"
                size="large"
                onClick={handleCancelReportNonPayment}
                className="no-fastclick"
              >
                Cancel
              </StyledButton>
              <StyledButton
                variant="contained"
                size="large"
                onClick={handleConfirmReportNonPayment}
                data-testid="complete-job-confirm-non-payment-button"
                className="no-fastclick"
                disabled={isReportingNonPayment}
              >
                {isReportingNonPayment ? 'Reporting…' : 'OK'}
              </StyledButton>
            </div>
          </>
        </SimpleModal>

        <SimpleModal isOpen={markPaidConfirmOpen}>
          <>
            <TitleContainer>
              <PopupTitle>Mark as Paid?</PopupTitle>
              <CrossButtonBlack onClick={handleCancelMarkPaid} />
            </TitleContainer>
            <ModalText>
              Mark this invoice as paid? This will update your records in TempStars.
            </ModalText>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
              <StyledButton
                variant="outlined"
                size="large"
                onClick={handleCancelMarkPaid}
                className="no-fastclick"
              >
                Cancel
              </StyledButton>
              <StyledButton
                variant="contained"
                size="large"
                onClick={handleConfirmMarkPaid}
                className="no-fastclick"
              >
                Confirm
              </StyledButton>
            </div>
          </>
        </SimpleModal>

        <SimpleModal isOpen={paymentMethodModalOpen}>
          <>
            <TitleContainer>
              <PopupTitle>How did you get paid for this shift?</PopupTitle>
              <CrossButtonBlack onClick={handleCancelPaymentMethod} />
            </TitleContainer>
            <div style={{ display: 'grid', rowGap: 12, marginTop: 8 }}>
              {PAYMENT_METHOD_OPTIONS.map(option => (
                <label
                  key={option.value}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
                >
                  <input
                    type="radio"
                    name="payment-method"
                    value={option.value}
                    checked={selectedPaymentMethod === option.value}
                    onChange={() => handleSelectPaymentMethod(option.value)}
                    className="no-fastclick"
                  />
                  <span style={{ color: 'var(--text-primary)', fontSize: 14 }}>{option.label}</span>
                </label>
              ))}
              {selectedPaymentMethod === 'other' && (
                <div style={{ display: 'grid', rowGap: 8 }}>
                  <input
                    type="text"
                    value={otherPaymentMethodText}
                    onChange={(e) => setOtherPaymentMethodText(e.target.value)}
                    placeholder="Describe the method (min 5 chars)"
                    maxLength={100}
                    style={{ padding: 10, borderRadius: 8, border: '1px solid var(--divider)', fontSize: 14 }}
                  />
                  {showOtherHelper && (
                    <span style={{ color: 'var(--primary-red)', fontSize: 12 }}>
                      Please enter at least 5 characters.
                    </span>
                  )}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
              <StyledButton
                variant="outlined"
                size="large"
                onClick={handleCancelPaymentMethod}
                className="no-fastclick"
                disabled={isMarkingPaid}
              >
                Cancel
              </StyledButton>
              <StyledButton
                variant="contained"
                size="large"
                onClick={handleConfirmPaymentMethod}
                className="no-fastclick"
                disabled={!isPaymentMethodValid || isMarkingPaid}
              >
                {isMarkingPaid ? 'Saving…' : 'Confirm'}
              </StyledButton>
            </div>
          </>
        </SimpleModal>

        <SimpleModal isOpen={successModalOpen}>
          <>
            <TitleContainer>
              <PopupTitle>Success!</PopupTitle>
              <CrossButtonBlack onClick={() => setSuccessModalOpen(false)} />
            </TitleContainer>
            <ModalText>
              {reportConfirmation}
            </ModalText>
            <StyledButton
              variant="contained"
              size="large"
              onClick={() => setSuccessModalOpen(false)}
              className="no-fastclick"
            >
              Ok
            </StyledButton>
          </>
        </SimpleModal>

        <SimpleModal isOpen={errorModalOpen}>
          <>
            <TitleContainer>
              <PopupTitle>Something went wrong</PopupTitle>
              <CrossButtonBlack onClick={() => setErrorModalOpen(false)} />
            </TitleContainer>
            <ModalText>
              {reportError || 'Error reporting non-payment. Please try again.'}
            </ModalText>
            <StyledButton
              variant="contained"
              size="large"
              onClick={() => setErrorModalOpen(false)}
              className="no-fastclick"
            >
              Close
            </StyledButton>
          </>
        </SimpleModal>

        <SimpleModal isOpen={isDownloadErrorOpen}>
          <>
            <TitleContainer>
              <PopupTitle>Unable to download invoice</PopupTitle>
              <CrossButtonBlack onClick={() => setIsDownloadErrorOpen(false)} />
            </TitleContainer>
            <ModalText>
              {downloadError || 'Unable to download invoice. Please try again.'}
            </ModalText>
            <StyledButton
              variant="contained"
              size="large"
              onClick={() => setIsDownloadErrorOpen(false)}
              className="no-fastclick"
            >
              Close
            </StyledButton>
          </>
        </SimpleModal>
      </ContentWrapper>
      
      <FixedLoader loading={isSaving || isLoadingInvoiceData || isDownloadingInvoice} />
      </PageContainer>

      {/* Invoice Modal */}
      {invoiceJobData && (
        <InvoiceModal
          job={invoiceJobData}
          isViewInvoice={invoiceModalIsViewMode}
          isOpen={isInvoiceModalOpen}
          onClose={handleInvoiceModalClose}
          onSuccess={handleInvoiceSuccess}
        />
      )}
    </>
  );
};

export default JobCompletedPage;
